import React, { useState } from 'react';
import {
  Card,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Input,
  Spinner,
  Alert,
} from 'reactstrap';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import { ShopListItem } from 'components/Types/Tenant/shop.type';
import { useListQuery } from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import EmptyState from 'components/Common/EmptyState';
import { SHOP_PATHS } from '../../../routes/routesConstants';

const DEFAULT_SHOP_COVER = 'https://placehold.co/800x200?text=Cover+Image';
const DEFAULT_SHOP_LOGO = 'https://placehold.co/100x100?text=Logo';

const TenantShopsList: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('1');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const { data, isLoading, isError } = useListQuery<ShopListItem>(
    `${API_ENDPOINTS.TENANT.SHOPS.BASE}`,
  );

  const shops: ShopListItem[] = data ?? [];

  const toggleTab = (tab: string) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setCurrentPage(1);
    }
  };

  const totalPages = Math.ceil(shops.length / pageSize);
  const paginatedShops = shops.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(event.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const renderPagination = () => {
    const pages: JSX.Element[] = [];
    const showPages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    const endPage = Math.min(totalPages, startPage + showPages - 1);

    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
          <Link to="#" className="page-link" onClick={() => handlePageChange(i)}>
            {i}
          </Link>
        </li>,
      );
    }

    return (
      <ul className="pagination justify-content-center pagination-rounded">
        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
          <Link to="#" className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
            <i className="mdi mdi-chevron-left"></i>
          </Link>
        </li>

        {pages}

        {endPage < totalPages && (
          <li className="page-item">
            <Link to="#" className="page-link">
              ...
            </Link>
          </li>
        )}

        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
          <Link to="#" className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
            <i className="mdi mdi-chevron-right"></i>
          </Link>
        </li>
      </ul>
    );
  };

  // ==============================
  // LOADING / ERROR
  // ==============================

  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row className="justify-content-center">
            <Col xl={10} className="text-center my-5">
              <Spinner color="primary" />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row className="justify-content-center">
            <Col xl={10}>
              <Alert color="danger" className="text-center">
                Failed to load shops. Please try again later.
              </Alert>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // ==============================
  // NO SHOPS → EMPTY STATE
  // ==============================

  if (!shops.length) {
    return (
      <EmptyState
        hideImage={true}
        message="No Shops Found"
        subMessage="You haven’t created any shops yet. Start by adding your first shop."
        buttonText="Add New Shop"
        buttonLink="/shops/add"
        imageSrc="https://placehold.co/300x200?text=No+Shops"
        imageAlt="No shops available"
      />
    );
  }

  // ==============================
  // SHOPS AVAILABLE → MAIN UI
  // ==============================

  return (
    <div className="page-content">
      <Container fluid>
        <Col xl={12} lg={8}>
          <Card>
            <Nav
              tag="ul"
              className="nav-tabs nav-tabs-custom justify-content-center pt-2"
              role="tablist"
            >
              <NavItem tag="li">
                <NavLink
                  to="#"
                  className={classnames({ active: activeTab === '1' })}
                  onClick={() => toggleTab('1')}
                >
                  All Shops
                </NavLink>
              </NavItem>

              <NavItem tag="li">
                <NavLink
                  to="#"
                  className={classnames({ active: activeTab === '2' })}
                  onClick={() => toggleTab('2')}
                >
                  Archive
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent className="p-4" activeTab={activeTab}>
              <TabPane tabId="1">
                <Row className="justify-content-center">
                  <Col xl={10}>
                    {/* HEADER ROW — Add Shop + Page Size */}
                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 mb-3">
                      {/* Add Shop Button */}
                      <Link to="/shops/add" className="btn btn-primary">
                        <i className="bx bx-plus-circle me-1"></i> Add New Shop
                      </Link>

                      {/* Page Size Dropdown */}
                      <Input
                        type="select"
                        className="form-select w-auto"
                        value={pageSize}
                        onChange={handlePageSizeChange}
                      >
                        <option value={10}>10 per page</option>
                        <option value={20}>20 per page</option>
                        <option value={50}>50 per page</option>
                      </Input>
                    </div>

                    <hr className="mb-4" />

                    {/* SHOP GRID */}
                    <Row className="g-4">
                      {paginatedShops.map((shop) => (
                        <Col sm={4} key={shop.id}>
                          <Card className="border-0 shadow-sm overflow-hidden">
                            <div style={{ height: '120px', overflow: 'hidden' }}>
                              <img
                                src={shop.coverImageUrl?.trim() || DEFAULT_SHOP_COVER}
                                alt={`${shop.name} cover`}
                                className="w-100 h-100"
                                style={{ objectFit: 'cover' }}
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = DEFAULT_SHOP_COVER;
                                }}
                              />
                            </div>

                            <div className="d-flex justify-content-center">
                              <div
                                className="bg-white border rounded-circle shadow-sm overflow-hidden d-flex align-items-center justify-content-center"
                                style={{ width: '80px', height: '80px', marginTop: '-40px' }}
                              >
                                <img
                                  src={shop.logoUrl?.trim() || DEFAULT_SHOP_LOGO}
                                  alt={`${shop.name} logo`}
                                  className="w-100 h-100"
                                  style={{ objectFit: 'cover' }}
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = DEFAULT_SHOP_LOGO;
                                  }}
                                />
                              </div>
                            </div>

                            <div className="p-3 text-center">
                              <h5 className="fw-semibold mt-2 mb-1">
                                <Link
                                  to={SHOP_PATHS.MEDICINE_SELL_DB_VIEW.replace(
                                    ':shopId',
                                    shop.id.toString(),
                                  )}
                                  className="text-primary text-decoration-none"
                                >
                                  {shop.name}
                                </Link>
                              </h5>

                              <span className="badge text-bg-primary">{shop.category}</span>

                              <ul className="list-unstyled mt-3 text-start">
                                <li className="mb-1">
                                  <i className="bx bx-map text-primary me-1"></i>
                                  <strong>Location:</strong> {shop.city}, {shop.state},{' '}
                                  {shop.country}
                                </li>

                                <li className="mb-1">
                                  <i className="bx bx-phone text-primary me-1"></i>
                                  <strong>Contact:</strong> {shop.contactNumber}
                                </li>

                                <li className="mb-1">
                                  <i className="bx bx-home text-primary me-1"></i>
                                  <strong>Address:</strong> {shop.streetAddress}
                                </li>
                              </ul>

                              <Link
                                to={SHOP_PATHS.MEDICINE_SELL_DB_VIEW.replace(
                                  ':shopId',
                                  shop.id.toString(),
                                )}
                                className="text-primary fw-semibold"
                              >
                                View Shop <i className="bx bx-right-arrow-alt align-middle"></i>
                              </Link>
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>

                    <hr className="my-4" />

                    <div className="text-center">{renderPagination()}</div>
                  </Col>
                </Row>
              </TabPane>

              <TabPane tabId="2">
                <Row className="justify-content-center">
                  <Col xl={8}>
                    <h5>Archive</h5>

                    <div className="mt-5">
                      <div className="d-flex flex-wrap">
                        <div className="ms-2">
                          <h4>2025</h4>
                        </div>
                        <div className="ms-auto">
                          <span className="badge badge-soft-success badge-pill ms-1 font-size-12">
                            {shops.length}
                          </span>
                        </div>
                      </div>

                      <hr className="mt-2" />

                      <div className="list-group list-group-flush">
                        {shops.map((shop) => (
                          <Link
                            key={shop.id}
                            to={`/shops/${shop.slug}`}
                            className="list-group-item text-muted"
                          >
                            <i className="mdi mdi-circle-medium ms-1"></i> {shop.name} - {shop.city}
                            , {shop.state}, {shop.country}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>
              </TabPane>
            </TabContent>
          </Card>
        </Col>
      </Container>
    </div>
  );
};

export default TenantShopsList;
